// @flow
import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import * as reactRouter from 'react-router-redux';
import linestring from 'turf-linestring';
import turf from 'turf';

import BusMarker from '../BusMarker';
import styles from './bus.css';
import * as selectors from '../../reducers';

import { setCurrentRoute, clearCurrentRoute } from '../../actions/routes';

const addBusGeoJson = (id: number, latitude: number, longitude: number) => ({
  type: 'Feature',
  geometry: {
    type: 'Point',
    coordinates: [longitude, latitude],
  },
  id,
});

type BusPropTypes = {
  tripId: number,
  routeId: number,
  tripName: string,
  latitude: number,
  longitude: number,
  map: mapboxgl.Map,
  id: number,
  sourceName: string,
  name: string,
  setCurrentRoute: Function,
  push: Function,
  busCodeName: string,
  busId: ID_TYPE,
  isOffline: boolean,
  openBusMarkerPopup: Function,
  closeBusMarkerPopup: Function,
  monitor: any,
};

type BusStateTypes = {
  angle: number,
  marker: ?mapboxgl.Marker
};

class Bus extends Component<BusPropTypes, BusStateTypes> {
  constructor(props) {
    super(props);

    this.state = {
      marker: null,
    };
  }

  componentDidMount() {
    const {
      latitude,
      longitude,
      map,
      id,
      sourceName,
      routeId,
      push,
    } = this.props;

    const { marker } = this.state;
    const coordinates = [longitude, latitude];

    if (map && latitude && longitude) {
      // Search if tripId is already on the Geo JSON Map definition.
      if (marker === null) {
        const el = document.createElement('div');

        el.className = `${
          styles.bus
        }`;

        const geoJSON = map.getSource(sourceName)._data;
        geoJSON.features.push(
          addBusGeoJson(id, latitude, longitude),
        );
        map.getSource(sourceName).setData(geoJSON);

        // Bus id
        el.setAttribute('id', id.toString());
        // Redirect on marker click
        el.addEventListener('click', () => {
          push(`/admin/map/route/${routeId}`);
          clearCurrentRoute();
          setCurrentRoute(routeId);
        });

        const busMarker = new mapboxgl.Marker(el).setLngLat(coordinates).addTo(map);

        // add marker to map
        this.setState({
          marker: busMarker,
        });
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      latitude, longitude, id, map, sourceName,
    } = this.props;
    const nextLatitude = nextProps.latitude;
    const nextLongitude = nextProps.longitude;
    const { marker } = this.state;

    // Check for position change in order to update bus on map and angle.
    if (latitude !== nextLatitude && longitude !== nextLongitude) {
      const coordinates = [longitude, latitude];
      const newCoordinates = [nextLongitude, nextLatitude];
      const line = linestring([coordinates, newCoordinates]);
      const distance = turf.lineDistance(line, 'kilometers');
      let step = 0;
      const numSteps = 500; // Change this to set animation resolution

      const animate = () => {
        step += 1;
        if (step <= numSteps) {
          const curDistance = step / numSteps * distance;
          const iPoint = turf.along(line, curDistance, 'kilometers');

          if (marker) {
            marker.setLngLat(iPoint.geometry.coordinates);
          }

          //  Refresh bus position
          const geoJSON = map.getSource(sourceName)._data;

          for (let i = 0; i < geoJSON.features.length; i += 1) {
            const current = geoJSON.features[i];

            if (current.id === id) {
              geoJSON.features[i] = addBusGeoJson(
                id,
                iPoint.geometry.coordinates[1],
                iPoint.geometry.coordinates[0],
              );
            }
          }

          // Refresh map
          map.getSource(sourceName).setData(geoJSON);

          requestAnimationFrame(animate);
        }
      };

      animate();
    }
  }

  componentWillUnmount() {
    const {
      latitude, longitude, map, id, sourceName,
    } = this.props;
    const { marker } = this.state;
    if (map && latitude && longitude) {
      // Search if tripId is already on the Geo JSON Map definition.
      if (marker !== null) {
        // create a DOM element for the marker
        const el = document.getElementById(id.toString());
        const geoJSON = map.getSource(sourceName)._data;
        // Remove Geo JSON
        const index = geoJSON.features.indexOf(addBusGeoJson(id, latitude, longitude));
        if (index > -1) {
          geoJSON.features.splice(index, 1);
        }
        map.getSource(sourceName).setData(geoJSON);

        // remove marker from map
        if (marker) marker.remove();
        if (el) el.remove();
      }
    }
  }

  _interval: IntervalID;

  render() {
    const {
      busCodeName,
      busId,
      tripName,
      monitor,
      nextStop,
      isOffline,
    } = this.props;

    const markerDomElement = document.getElementById(busId);

    return !!markerDomElement
      ? createPortal(
        <BusMarker
          busCodeName={busCodeName}
          tripName={tripName}
          monitor={monitor}
          nextStopName={nextStop.name}
          isOffline={isOffline}
        />,
        markerDomElement,
      )
      : null;
  }
}


export default connect(
  (state, { tripId }) => {
    const trip = selectors.getTrip(state, tripId);
    const routeStops = selectors.getRouteStops(state, trip.route);

    let name;
    if (trip) {
      name = trip.name;
    }
    let location = selectors.getBusLocation(state, trip.bus);
    if (!location) {
      if (routeStops.length > 0) {
        if (routeStops[0].point) {
          const firstStopCoords = routeStops[0].point.coordinates;
          location = { longitude: firstStopCoords[0], latitude: firstStopCoords[1] };
        }
      }
    }
    const nextStop = selectors.getNextTripStop(state, tripId);
    const currentTimestamp = selectors.getCurrentTimestamp(state);
    const bus = selectors.getBus(state, trip.bus);
    const monitor = selectors.getMonitor(state, trip.monitor);

    if (typeof nextStop !== 'undefined') {
      return {
        currentTimestamp,
        ...location,
        nextStop,
        monitor,
        busCodeName: bus.code_name,
        busId: bus.id,
        isOffline: bus.isOffline,
        eta: selectors.getTripStopETA(state, nextStop.id),
        routeId: trip.route,
        tripName: trip.name,
        name,
      };
    }
    return {
      currentTimestamp,
      routeId: trip.route,
      tripName: trip.name,
      busCodeName: bus.code_name,
      busId: bus.id,
      isOffline: bus.isOffline,
      name,
      monitor,
      ...location,
      nextStop: {
        name: 'Viaje concluido',
        eta: 0,
      },
    };
  },
  dispatch => ({
    push: (route: string) => dispatch(reactRouter.push(route)),
    setCurrentRoute: (routeId: number) => dispatch(setCurrentRoute(routeId)),
    clearCurrentRoute: () => dispatch(clearCurrentRoute()),
  }),
)(Bus);



// WEBPACK FOOTER //
// ./src/scripts/components/Bus/index.jsx